/**
 * Phase 2.4 — Narrative generator for telecom infrastructure controls.
 *
 * Pure functions that turn (control_key, status) into a consulting-grade
 * sentence sourced from INFRASTRUCTURE_CONTROL_METADATA. Renderers must
 * NOT hardcode narrative strings; always call into this module.
 */
import {
  INFRASTRUCTURE_CONTROL_METADATA,
  type InfrastructureControlMetadata,
} from "@/configs/controlGuidance/infrastructureControlMetadata";

export type ControlStatus =
  | "Implemented"
  | "Partially Implemented"
  | "Not Implemented"
  | "Not Assessed"
  | string;

function normalize(status: string | null | undefined): ControlStatus {
  const s = (status ?? "").trim().toLowerCase();
  if (s === "implemented") return "Implemented";
  if (s === "partially implemented" || s === "partial") return "Partially Implemented";
  if (s === "not implemented") return "Not Implemented";
  return "Not Assessed";
}

export function buildInfrastructureControlNarrative(
  controlKey: string,
  status: string | null | undefined,
): string | null {
  const meta: InfrastructureControlMetadata | undefined =
    INFRASTRUCTURE_CONTROL_METADATA[controlKey];
  if (!meta) return null;

  const label = meta.label;
  const why = meta.whyItMatters;
  const norm = normalize(status);

  switch (norm) {
    case "Implemented":
      return `${label} is implemented. ${why}`;
    case "Partially Implemented":
      return `${label} is partially implemented. ${why} Residual gaps should be closed to fully realize the control's protective intent.`;
    case "Not Implemented":
      return `${label} is not implemented. ${why} This represents a material exposure that warrants prioritized remediation.`;
    default:
      return `${label} has not been assessed in this engagement.`;
  }
}

/**
 * Executive summary paragraph for the telecom block.
 * No scores, no maturity bands — only counts and module names.
 */
export function buildTelecomExecutiveSummary(args: {
  serviceModels: string[];
  modulesAssessed: string[];
  counts: {
    controls_evaluated: number;
    implemented: number;
    partial: number;
    not_implemented: number;
    findings_count: number;
  };
}): string {
  const { serviceModels, modulesAssessed, counts } = args;
  const sm = serviceModels.length > 0 ? serviceModels.join(", ") : "the assessed telecom estate";
  const mods =
    modulesAssessed.length > 0
      ? modulesAssessed.join(", ").replace(/, ([^,]*)$/, " and $1")
      : "the assessed telecom modules";

  const sentences: string[] = [];
  sentences.push(
    `The Telecommunications Infrastructure Security assessment evaluated ${counts.controls_evaluated} telecom-specific control${counts.controls_evaluated === 1 ? "" : "s"} across ${mods} for ${sm}.`,
  );
  if (counts.controls_evaluated > 0) {
    sentences.push(
      `${counts.implemented} control${counts.implemented === 1 ? " was" : "s were"} Implemented, ${counts.partial} Partially Implemented, and ${counts.not_implemented} Not Implemented.`,
    );
  }
  if (counts.findings_count > 0) {
    sentences.push(
      `${counts.findings_count} infrastructure finding${counts.findings_count === 1 ? " was" : "s were"} generated and flow into the consolidated Findings, Remediation Roadmap, and Corrective Action Plan.`,
    );
  } else if (counts.controls_evaluated > 0) {
    sentences.push("No infrastructure findings were generated during this review.");
  }
  return sentences.join(" ");
}
